import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Button } from "@design-system/Button/Button";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { ArrowCornerIcon } from "@icons";
import { CategoryGlMapping } from "@common/contracts/category.contract";
import { createRulesUrl } from "@components/settings/Rules/lib/rulesUrl";
import { RuleGroupType } from "@common/types/transactionRule";
import { TransactionCategoryRow } from "../viewUtils";
import styles from "../Categories.module.scss";

function getUniformLabel(glMapping: CategoryGlMapping): string {
    const { inAccountName, outAccountName } = glMapping;
    if (inAccountName && outAccountName) {
        return inAccountName === outAccountName
            ? inAccountName
            : `In: ${inAccountName} · Out: ${outAccountName}`;
    }
    return inAccountName ?? outAccountName ?? "Default";
}

const DIVERGENT_LABEL_CUSTOM = "Custom · varies by entity";
const DIVERGENT_LABEL_BUILTIN = "Varies by entity";

export const GlMappingCell = memo(function GlMappingCell({
    row,
}: CellContext<ViewTableFeatures, TransactionCategoryRow, unknown>) {
    const { parentId, glMapping, category } = row.original;

    if (parentId === null || !glMapping) {
        return <ViewTableCell />;
    }

    if (glMapping.state === "advanced") {
        const rulesPath = createRulesUrl({
            tab: RuleGroupType.ACCOUNTING,
            filters: { categoryId: glMapping.categoryId },
        });
        return (
            <ViewTableCell>
                <Button
                    variant="ghost"
                    asChild
                    className={styles.glMappingButton}
                >
                    <StateAwareLink
                        to={rulesPath}
                        className="text-decoration-none"
                    >
                        Advanced rule
                        <ArrowCornerIcon />
                    </StateAwareLink>
                </Button>
            </ViewTableCell>
        );
    }

    if (glMapping.state === "uniform") {
        return (
            <ViewTableCell>
                <span className={styles.glMapping}>
                    {getUniformLabel(glMapping)}
                </span>
            </ViewTableCell>
        );
    }

    if (glMapping.state === "divergent") {
        return (
            <ViewTableCell>
                <span className={styles.glMapping}>
                    {category.workspaceId !== null
                        ? DIVERGENT_LABEL_CUSTOM
                        : DIVERGENT_LABEL_BUILTIN}
                </span>
            </ViewTableCell>
        );
    }

    return (
        <ViewTableCell>
            <span className={styles.glMapping}>Default</span>
        </ViewTableCell>
    );
});
